import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Button } from 'components/atoms/Button';
import { useTextInput } from 'components/atoms/Input';
import { InputLabel } from 'components/atoms/InputLabel';
import { ButtonModal } from 'components/organisms/ButtonModal';
import { PhoneVerification } from '../PhoneVerification';

import { useAuth } from 'libs/AuthContext';
import { media } from 'utils';

import * as fonts from 'styles/fonts';
import * as mixins from 'styles/mixins';

function FindPasswordForm() {
  const history = useHistory();
  const {
    findPassword,
    findInfo: { findPasswordSuccess, findPasswordFailMsg, phoneVerified, phoneNumber },
  } = useAuth();

  const [showButtonModal, setShowButtonModal] = useState(false);

  const [name, nameInput] = useTextInput({
    id: 'nameInput',
    placeholder: '이름을 입력해주세요',
  });

  const [username, usernameInput] = useTextInput({
    id: 'usernameInput',
    placeholder: '아이디를 입력해주세요.',
  });

  const findPasswordBtnEnabled = useMemo(
    () => !!name && !!phoneNumber && phoneVerified && !!username,
    [name, phoneNumber, phoneVerified, username],
  );

  const closeButtonModal = () => {
    setShowButtonModal(false);

    if (findPasswordSuccess) return history.push('/login');
  };

  const confirmMessage = useMemo(() => {
    if (findPasswordSuccess) {
      return (
        <>
          <ModalText>문자 또는 카카오톡으로</ModalText>
          <ModalText>임시 비밀번호가 발송되었습니다.</ModalText>
        </>
      );
    }

    if (findPasswordFailMsg) {
      return <ModalText>{findPasswordFailMsg}</ModalText>;
    }
  }, [findPasswordSuccess, findPasswordFailMsg]);

  const handleGoBack = () => {
    history.push('/login');
  };

  const handleFindPasswordBtnClick = () => {
    setShowButtonModal(true);
    findPassword(name, username, phoneNumber);
  };

  return (
    <>
      <ButtonModal
        buttons={[{ type: 'close', name: '확인', action: () => closeButtonModal() }]}
        isOpen={showButtonModal}
      >
        {confirmMessage}
      </ButtonModal>
      <FormContainer>
        <div>
          <Header>
            <BackArrowIcon onClick={() => handleGoBack()} />
            <MainText>비밀번호 찾기</MainText>
          </Header>
          <InputWrap error={false}>
            <InputLabel htmlFor="nameInput">이름</InputLabel>
            {nameInput}
          </InputWrap>
          <InputWrap error={false}>
            <InputLabel htmlFor="usernameInput">아이디</InputLabel>
            {usernameInput}
          </InputWrap>
          <PhoneVerification />
        </div>
        <FindPasswordBtn
          primary
          disabled={!findPasswordBtnEnabled}
          size="mid"
          onClick={() => handleFindPasswordBtnClick()}
        >
          비밀번호 찾기
        </FindPasswordBtn>
      </FormContainer>
    </>
  );
}

export default FindPasswordForm;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  box-sizing: border-box;
  background-color: var(--white);

  ${media.main} {
    max-width: 675px;
    max-height: 1112px;
  }

  input {
    flex: 1;
    &:focus {
      font-size: 14px;
    }
  }
`;

const Header = styled.header`
  ${mixins.flexCenter}
  position: relative;
  margin-bottom: 52px;
`;

const BackArrowIcon = styled.div`
  position: absolute;
  left: 0px;
  width: 32px;
  height: 32px;
  background-image: url('/icons/ic-back-arrow.svg');
  cursor: pointer;
`;

const MainText = styled.h1`
  font-family: NotoSansMedium;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 27px;
  color: var(--gray-600);
  margin: 0;
`;

const InputWrap = styled.div<{ error: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 26px;

  ${({ error }) =>
    error &&
    css`
      margin-bottom: 8px;
    `}

  input {
    flex: 1;
  }
`;

const Disabled = css`
  color: var(--gray-400);
  background-color: var(--gray-80);
  border: 1px solid var(--gray-150);
`;

const FindPasswordBtn = styled(Button)<{ disabled: boolean }>`
  ${fonts.normal};
  ${fonts.demi};
  color: var(--white);
  font-size: 20px;
  width: 100%;
  height: 60px;
  ${({ disabled }) => (disabled ? Disabled : '')}
`;

const ModalText = styled.span`
  color: #777777;
  font-family: NotoSansRegular;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  white-space: pre-wrap;
`;
